// INF-821 — Operations surface Phase 1: the /ops build board (read-only).
//
// The Monday.com-replacement wedge (INF-225): active builds (post-deposit via
// the INF-450 handoff spine, pre-delivery) with stage, days-to-promised
// (INF-470 — SPARSE data: NULL renders as "Unscheduled", never an error),
// handoff state, and the needs-list with the v18 procurement overlay.
// Data: GET /api/ops/board (cookie SSO). No writes — Phase 1 is read-only.

import { useEffect, useState } from 'react';

interface OpsNeed {
  line_id: number;
  product_id: number | null;
  name: string | null;
  qty: number | null;
  procurement_status: 'not_ordered' | 'rfq' | 'ordered' | 'received';
  po_names: string | null;
  po_qty: number | null;
  qty_received: number | null;
  po_eta: string | null;
}

interface OpsBuild {
  odoo_sale_order_id: number;
  so_name: string | null;
  partner_name: string | null;
  state: string | null;
  invoice_status: string | null;
  amount_total: number | null;
  commitment_date: string | null;
  days_to_promised: number | null;
  greenlit_at: string;
  handoff_linked: boolean;
  stage: 'no_materials' | 'greenlit' | 'procuring' | 'materials_ready';
  blocked: boolean;
  blocked_reasons: string[];
  readiness_band: 'green' | 'yellow' | 'red';
  readiness_reasons: string[];
  needs_total: number;
  needs_received: number;
  needs: OpsNeed[];
}

interface OpsBoardPayload {
  generated_at: string;
  builds: OpsBuild[];
}

type State =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; board: OpsBoardPayload };

const STAGE_LABELS: Record<OpsBuild['stage'], { label: string; cls: string }> = {
  no_materials: { label: 'No materials', cls: 'bg-slate-100 text-slate-600' },
  greenlit: { label: 'Greenlit', cls: 'bg-sky-100 text-sky-700' },
  procuring: { label: 'Procuring', cls: 'bg-amber-100 text-amber-700' },
  materials_ready: { label: 'Materials ready', cls: 'bg-emerald-100 text-emerald-700' },
};

// INF-1183 — derived production-readiness band (the Forecast non-owning-projection ADR).
// Provisional, read-only; computed server-side in src/ops/board.ts. Owns nothing.
const READINESS_LABELS: Record<OpsBuild['readiness_band'], { label: string; cls: string; dot: string }> = {
  green: { label: 'On track', cls: 'bg-emerald-100 text-emerald-700', dot: 'bg-emerald-500' },
  yellow: { label: 'Watch', cls: 'bg-amber-100 text-amber-700', dot: 'bg-amber-500' },
  red: { label: 'At risk', cls: 'bg-rose-100 text-rose-700', dot: 'bg-rose-500' },
};

const PROCUREMENT_LABELS: Record<OpsNeed['procurement_status'], { label: string; cls: string }> = {
  not_ordered: { label: 'Not ordered', cls: 'bg-rose-100 text-rose-700' },
  rfq: { label: 'RFQ', cls: 'bg-amber-100 text-amber-700' },
  ordered: { label: 'Ordered', cls: 'bg-sky-100 text-sky-700' },
  received: { label: 'Received', cls: 'bg-emerald-100 text-emerald-700' },
};

const BLOCKED_REASON_LABELS: Record<string, string> = {
  unordered_past_lead_time: 'Needed line(s) un-ordered past lead time',
};

function formatMoney(v: number | null): string {
  if (v == null) return '—';
  return v.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
}

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return iso.slice(0, 10);
}

// INF-470 sparse-data contract: NULL commitment_date is the NORMAL case today.
function promisedChip(b: OpsBuild): JSX.Element {
  if (b.days_to_promised == null) {
    return (
      <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-500">
        Unscheduled
      </span>
    );
  }
  const overdue = b.days_to_promised < 0;
  const tight = b.days_to_promised >= 0 && b.days_to_promised <= 14;
  const cls = overdue
    ? 'bg-rose-100 text-rose-700'
    : tight
      ? 'bg-amber-100 text-amber-700'
      : 'bg-emerald-100 text-emerald-700';
  const text = overdue
    ? `${Math.abs(b.days_to_promised)}d overdue`
    : `${b.days_to_promised}d to promised`;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`} title={`Promised ${formatDate(b.commitment_date)}`}>
      {text}
    </span>
  );
}

function BuildCard({ build }: { build: OpsBuild }): JSX.Element {
  const [open, setOpen] = useState(false);
  const stage = STAGE_LABELS[build.stage] ?? STAGE_LABELS.no_materials;
  const readiness = READINESS_LABELS[build.readiness_band] ?? READINESS_LABELS.green;

  return (
    <div className={`rounded-lg bg-white ring-1 ${build.blocked ? 'ring-rose-300' : 'ring-slate-200'}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-left"
      >
        <span className="font-semibold text-slate-900">{build.so_name ?? `SO ${build.odoo_sale_order_id}`}</span>
        <span className="text-sm text-slate-600 truncate">{build.partner_name ?? 'Unknown partner'}</span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${readiness.cls}`}
          title={`Readiness (provisional): ${build.readiness_reasons.join(' · ')}`}
        >
          <span className={`h-1.5 w-1.5 rounded-full ${readiness.dot}`} />
          {readiness.label}
        </span>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${stage.cls}`}>
          {stage.label}
        </span>
        {promisedChip(build)}
        <span
          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
            build.handoff_linked ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'
          }`}
          title="Scope-sheet linkage on the production handoff (display-only in Phase 1)"
        >
          {build.handoff_linked ? 'Handoff linked' : 'Handoff unlinked'}
        </span>
        {build.blocked && (
          <span className="inline-flex items-center rounded-full bg-rose-600 px-2 py-0.5 text-xs font-semibold text-white">
            Blocked
          </span>
        )}
        <span className="ml-auto flex items-center gap-3 text-xs text-slate-500">
          <span>
            {build.needs_received}/{build.needs_total} received
          </span>
          <span>{formatMoney(build.amount_total)}</span>
          <span className="text-slate-400">{open ? '▾' : '▸'}</span>
        </span>
      </button>

      {build.blocked && build.blocked_reasons.length > 0 && (
        <div className="px-4 pb-2 -mt-1">
          <p className="text-xs text-rose-700">
            {build.blocked_reasons.map((r) => BLOCKED_REASON_LABELS[r] ?? r).join(' · ')}
          </p>
        </div>
      )}

      {open && (
        <div className="border-t border-slate-100 px-4 py-3">
          <p className="text-xs text-slate-500 mb-2">
            Greenlit {formatDate(build.greenlit_at)} · promised {formatDate(build.commitment_date)} · invoice status{' '}
            {build.invoice_status ?? '—'}
          </p>
          {build.needs.length === 0 ? (
            <p className="text-sm text-slate-500">No material lines on this order.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500">
                  <th className="py-1 pr-3 font-medium">Needed line</th>
                  <th className="py-1 pr-3 font-medium w-16">Qty</th>
                  <th className="py-1 pr-3 font-medium w-28">Status</th>
                  <th className="py-1 pr-3 font-medium w-32">PO</th>
                  <th className="py-1 font-medium w-24">ETA</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {build.needs.map((n) => {
                  const st = PROCUREMENT_LABELS[n.procurement_status] ?? PROCUREMENT_LABELS.not_ordered;
                  return (
                    <tr key={n.line_id}>
                      <td className="py-1.5 pr-3 text-slate-800">{n.name ?? `Product ${n.product_id ?? '?'}`}</td>
                      <td className="py-1.5 pr-3 text-slate-600">{n.qty ?? '—'}</td>
                      <td className="py-1.5 pr-3">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${st.cls}`}>
                          {st.label}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3 text-slate-600 truncate" title={n.po_names ?? undefined}>
                        {n.po_names ?? '—'}
                      </td>
                      <td className="py-1.5 text-slate-600">{formatDate(n.po_eta)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}

export function OpsBoard(): JSX.Element {
  const [state, setState] = useState<State>({ kind: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/ops/board', { credentials: 'include' });
        if (cancelled) return;
        if (!res.ok) {
          const body = (await res.json().catch(() => null)) as { error?: string } | null;
          setState({ kind: 'error', message: body?.error ?? `Unexpected status ${res.status}` });
          return;
        }
        const board = (await res.json()) as OpsBoardPayload;
        setState({ kind: 'ready', board });
      } catch (err) {
        if (cancelled) return;
        setState({ kind: 'error', message: (err as Error).message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.kind === 'loading') {
    return <p className="text-slate-500">Loading builds…</p>;
  }
  if (state.kind === 'error') {
    return (
      <div className="rounded-lg bg-white p-6 ring-1 ring-slate-200">
        <h2 className="text-lg font-semibold text-slate-900">Ops Board</h2>
        <p className="mt-2 text-sm text-rose-600">Error: {state.message}</p>
      </div>
    );
  }

  const { board } = state;
  const atRiskCount = board.builds.filter((b) => b.readiness_band === 'red').length;
  const watchCount = board.builds.filter((b) => b.readiness_band === 'yellow').length;
  const unscheduledCount = board.builds.filter((b) => b.days_to_promised == null).length;

  return (
    <div className="w-full max-w-full overflow-x-hidden">
      <div className="flex flex-wrap items-start gap-x-3 gap-y-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-lg font-semibold text-slate-900">Build readiness readout</h2>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600 ring-1 ring-slate-200">
              Read-only Odoo projection
            </span>
          </div>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Active builds, material readiness, handoff linkage, and promised-date risk. Use it to diagnose production
            readiness; change orders, dates, and purchase state in their source systems. Readiness is a provisional
            derived hint, not a commitment.
          </p>
        </div>
        <p className="ml-auto text-xs text-slate-400">as of {board.generated_at.slice(0, 19).replace('T', ' ')}</p>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-4">
        <ReadoutMetric label="Active builds" value={board.builds.length} />
        <ReadoutMetric label="At risk" value={atRiskCount} tone={atRiskCount > 0 ? 'warn' : 'default'} />
        <ReadoutMetric label="Watch" value={watchCount} tone={watchCount > 0 ? 'warn' : 'default'} />
        <ReadoutMetric label="Unscheduled" value={unscheduledCount} tone={unscheduledCount > 0 ? 'warn' : 'default'} />
      </div>

      {board.builds.length === 0 ? (
        <div className="mt-4 rounded-lg bg-white p-8 ring-1 ring-slate-200">
          <p className="text-sm text-slate-600">No active builds.</p>
          <p className="mt-1 text-xs text-slate-500">
            Builds appear here when their deposit is paid (the production greenlight) and drop off when fully
            invoiced. If this looks wrong, check that the Odoo mirrors and the INF-450 handoff consumer are running.
          </p>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          {board.builds.map((b) => (
            <BuildCard key={b.odoo_sale_order_id} build={b} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReadoutMetric({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'warn';
}): JSX.Element {
  return (
    <div className={`rounded-lg bg-white p-4 ring-1 ${tone === 'warn' ? 'ring-amber-200' : 'ring-slate-200'}`}>
      <div className={`text-2xl font-semibold ${tone === 'warn' ? 'text-amber-700' : 'text-slate-900'}`}>{value}</div>
      <div className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
    </div>
  );
}
